const resolveFeed = (feedOrRef) => {
  if (!feedOrRef) return null;
  if (Object.prototype.hasOwnProperty.call(feedOrRef, "current")) {
    return feedOrRef.current || null;
  }
  return feedOrRef;
};

const scheduledBottomScrollByFeed = new WeakMap();

export function scrollChatFeedToBottom(feedOrRef) {
  const feed = resolveFeed(feedOrRef);
  if (!feed) return false;

  try {
    feed.scrollTop = feed.scrollHeight;
    return true;
  } catch {
    return false;
  }
}

/**
 * Keep a chat pinned while React commits the sent message and the composer
 * collapses back to its resting height. The delayed passes also cover image
 * measurements and mobile VisualViewport updates that land just after paint.
 */
export function scheduleChatFeedScrollToBottom(feedOrRef) {
  const feed = resolveFeed(feedOrRef);
  if (!feed) return () => {};

  // Sending a message can request the same bottom pin from the send handler,
  // the items-length effect, and the request completion. Cancel the older
  // delayed passes so they cannot keep forcing layout during a native keyboard
  // or composer transition.
  scheduledBottomScrollByFeed.get(feed)?.cancel();

  const frameIds = [];
  const timerIds = [];
  const scheduleToken = {};
  const requestFrame =
    typeof window !== "undefined" && typeof window.requestAnimationFrame === "function"
      ? window.requestAnimationFrame.bind(window)
      : null;
  const cancelFrame =
    typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function"
      ? window.cancelAnimationFrame.bind(window)
      : null;

  const scroll = () => scrollChatFeedToBottom(feed);

  const cancel = () => {
    if (cancelFrame) frameIds.forEach((id) => cancelFrame(id));
    if (typeof window !== "undefined") {
      timerIds.forEach((id) => window.clearTimeout(id));
    }
    const current = scheduledBottomScrollByFeed.get(feed);
    if (current?.token === scheduleToken) {
      scheduledBottomScrollByFeed.delete(feed);
    }
  };

  scheduledBottomScrollByFeed.set(feed, { token: scheduleToken, cancel });

  scroll();

  if (requestFrame) {
    frameIds.push(
      requestFrame(() => {
        scroll();
        frameIds.push(requestFrame(scroll));
      })
    );
  }

  if (typeof window !== "undefined") {
    [80, 220].forEach((delay, index, delays) => {
      timerIds.push(window.setTimeout(() => {
        scroll();
        if (index === delays.length - 1) {
          const current = scheduledBottomScrollByFeed.get(feed);
          if (current?.token === scheduleToken) {
            scheduledBottomScrollByFeed.delete(feed);
          }
        }
      }, delay));
    });
  }

  return cancel;
}
